import Ajv from 'ajv';
import { z } from 'zod';
import {
  CountryCodesConst,
  Opportunity as OpportunityConst,
  OpportunitiesConst,
  YesNoMaybeConst,
  PracticeArea,
  FocusArea,
} from '@axiom/const';
import {
  SchemaDate,
  SchemaLocation,
  SchemaTimestamp,
  SchemaCurrency,
  ZodArrayToEnum,
} from '@axiom/types';

import { ContactSchema } from './contacts';
import { EventSchema } from './event';
import { axiomValidationOptions } from './options';
import { UserSchema } from './user';
import { arrayValidatorCreator } from './general';
import { AccountSchema } from './account';
import { PositionSchema } from './position';
import { OpportunityLanguageSchema } from './opportunity-language';
import { getMaxFloatValue } from './utils';
import { TimeToSubmissionSchema } from './time-to-submission';

const { StageCodes, TransactionSubType } = OpportunitiesConst;

export const OpportunityStageCodes = Object.values(
  OpportunityConst.STAGE_CODE_MAP
);

export const OpportunityStages = OpportunityConst.STAGES;
export const OpportunityTransactionSubTypes = Object.values(TransactionSubType);

export const OpportunityTalentInMinds = Object.keys(
  YesNoMaybeConst.YesNoMaybe
) as Array<keyof typeof YesNoMaybeConst.YesNoMaybe>;

export const OpportunityLaptopProviders = Object.keys(
  OpportunitiesConst.LaptopProvider
) as Array<keyof typeof OpportunitiesConst.LaptopProvider>;

export const OpportunityAddressCountryCodes = Object.keys(
  CountryCodesConst.CountryCodesAbbreviations
) as Array<keyof typeof CountryCodesConst.CountryCodesAbbreviations>;

export const OpportunityWorkLocations = Object.keys(
  OpportunitiesConst.WorkLocation
) as Array<keyof typeof OpportunitiesConst.WorkLocation>;

export const OpportunityPrimaryPracticeAreas = Object.keys(
  PracticeArea
) as Array<keyof typeof PracticeArea>;

export const OpportunityPrimaryFocusAreas = Object.keys(FocusArea) as Array<
  keyof typeof FocusArea
>;

export const OpportunitySchema = z
  .object({
    account: AccountSchema.nullable(),
    accountId: z.string().uuid().nullable(),
    accountName: z.string().max(255).nullable(),
    accountSalesforceId: z.string().max(255).nullable(),
    addressCity: z.string().nullable(),
    addressCountryCode: z
      .enum(ZodArrayToEnum(OpportunityAddressCountryCodes))
      .nullable(),
    addressState: z.string().nullable(),
    addressStreet: z.string().nullable(),
    addressZip: z.string().nullable(),
    allowInterest: z.boolean().nullish().default(false),
    amount: z.number().nullable(),
    billingCurrency: z.string().max(255).nullable(),
    billingEstimatedUnits: z.string().max(255).nullable(),
    billingName: z.string().max(255).nullable(),
    businessTeam: z.string().max(255).nullable(),
    clientName: z.string().max(255).nullable(),
    closedDate: SchemaDate.nullable(),
    closedLostNotes: z.string().max(255).nullable(),
    competitorType: z.string().max(255).nullable(),
    competitorView: z.string().max(255).nullable(),
    confidential: z.boolean().nullable(),
    confidentialUpdatedBy: z.string().uuid().nullable(),
    confidentialUpdatedAt: SchemaTimestamp.nullable(),
    confidentialLastUpdatedByUser: UserSchema.nullable(),
    country: z.enum(ZodArrayToEnum(OpportunityAddressCountryCodes)).nullable(),
    createdAt: SchemaTimestamp,
    cultureSkills: z.string().max(255).nullable(),
    currency: SchemaCurrency.nullable(),
    deliveryManager: UserSchema.nullable(),
    deliveryManagerId: z.string().uuid(),
    description: z.string().nullable(),
    directEligible: z.boolean().default(false).nullable(),
    endDate: SchemaDate.nullable(),
    engagementType: z.string().max(255).nullable(),
    events: z.array(EventSchema).nullish(),
    excludedFromFeedUpdatedAt: SchemaTimestamp.nullable(),
    excludedFromFeedUpdatedBy: z.string().uuid().nullable(),
    excludedFromFeedUserOverride: z.boolean().nullish(),
    firstCandidateSelectedOrFurtherAt: SchemaTimestamp.nullable(),
    firstCandidateSubmittedAt: SchemaTimestamp.nullable(),
    forecastCategory: z.string().max(255).nullable(),
    fulfillmentStatus: z.string().max(255).nullable(),
    id: z.string().uuid(),
    inadequateSupplyCategory: z.string().max(255).nullable(),
    integrationId: z.string().max(255).nullable(),
    isClosed: z.boolean().default(false),
    isExcludedFromFeed: z.boolean(),
    isFalseStart: z.boolean(),
    isFulfillmentActive: z.boolean(),
    isInterviewing: z.boolean(),
    isQualificationComplete: z.boolean(),
    isRemote: z.boolean().nullable(),
    isSynced: z.boolean(),
    jobName: z.string().max(255).nullable(),
    languages: z.array(OpportunityLanguageSchema).nullish(),
    laptopProvider: z
      .enum(ZodArrayToEnum(OpportunityLaptopProviders))
      .nullable(),
    lastUpdatedBy: z.string().uuid().nullish(),
    locationCityStateCountry: z.string().nullable(),
    madeAvailableToFeedAt: SchemaTimestamp.nullable(),
    madeAvailableToFeedNotificationAt: SchemaTimestamp.nullable(),
    matterId: z.string().max(255).nullable(),
    nextStep: z.string().max(255).nullable(),
    offering: z.string().max(255).nullable(),
    onsiteRemote: z.string().max(255).nullable(),
    opportunityOwnerSalesforceId: z.string().max(255).nullable(),
    ownerUser: UserSchema.nullable(),
    ownerUserId: z.string().uuid(),
    positions: z.array(PositionSchema),
    primaryFocusArea: z.string().nullable(), // No direct access to these enums
    primaryPracticeArea: z.string().nullable(), // No direct access to these enums
    qualifiedAt: SchemaTimestamp.nullable(),
    reasonLost: z.string().nullable(),
    region: z.string().max(255).nullable(),
    renewal: z.string().max(255).nullable(),
    renewalContract: z.string().max(255).nullable(),
    requiredBillingHoursPerWeek: z.number().nullish(),
    requiredNumResources: z.number().int().nullable(),
    requiredTalentCountryCode: z
      .enum(ZodArrayToEnum(OpportunityAddressCountryCodes))
      .nullable(),
    salesCloseDate: SchemaDate.nullable(),
    salesforceAccountId: z.string().max(255).nullable(),
    salesforceContact: ContactSchema.nullable(),
    salesforceContactId: z.string().uuid(),
    salesforceContractId: z.string().max(255).nullable(),
    salesforceCxLead: ContactSchema,
    salesforceCxLeadId: z.string().uuid(),
    salesforceId: z.string().max(255).nullable(),
    salesforceQuoteId: z.string().max(255).nullable(),
    salesLead: UserSchema.nullable(),
    salesLeadId: z.string().uuid(),
    salesNote: z.string().nullable(),
    selfService: z.boolean().default(false),
    stage: z.enum(OpportunityStages).nullable(),
    stageCode: z.enum(ZodArrayToEnum(OpportunityStageCodes)).nullable(),
    startDate: SchemaDate.nullable(),
    statusUpdatedAt: SchemaTimestamp.nullable(),
    stopDirectInterestEmails: z.boolean().default(false),
    submissionId: z.string().uuid(),
    submissionLastUpdatedAt: SchemaTimestamp.nullable(),
    submissionPublishedAt: SchemaTimestamp.nullable(),
    talentInMind: z.enum(ZodArrayToEnum(OpportunityTalentInMinds)).nullable(),
    timeToSubmission: TimeToSubmissionSchema.nullable(),
    transactionSubType: z
      .enum(ZodArrayToEnum(OpportunityTransactionSubTypes))
      .nullable(),
    updatedAt: SchemaTimestamp.nullish(),
    useCase: z.string().max(255).nullable(),
    workLocation: z.enum(ZodArrayToEnum(OpportunityWorkLocations)).nullable(),
  })
  .merge(SchemaLocation);

export type Opportunity = z.infer<typeof OpportunitySchema>;

const ajv = new Ajv({
  ...axiomValidationOptions(),
  coerceTypes: true,
});

const ajvNoCoerce = new Ajv({
  ...axiomValidationOptions(),
});

// VALIDATIONS
const opportunityCommonValidation = {
  type: 'object',
  additionalProperties: false,
  properties: {
    accountName: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    accountSalesforceId: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    amount: {
      type: ['number', 'null'],
      maximum: Number.MAX_SAFE_INTEGER,
    },
    billingCurrency: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    billingEstimatedUnits: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    businessTeam: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    clientName: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    closedLostNotes: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    competitorType: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    competitorView: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    confidential: {
      type: 'boolean',
    },
    cultureSkills: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    currency: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    deliveryManagerId: {
      type: ['string', 'null'],
    },
    description: {
      type: ['string', 'null'],
    },
    engagementType: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    firstCandidateSubmittedAt: {
      type: ['string', 'null'],
      format: 'date-time',
    },
    firstCandidateSelectedOrFurtherAt: {
      type: ['string', 'null'],
      format: 'date-time',
    },
    forecastCategory: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    fulfillmentStatus: {
      type: ['string', 'null'],
      enum: OpportunitiesConst.FulfillmentStatuses.enumValues.map(
        value => value.v
      ),
    },
    inadequateSupplyCategory: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    integrationId: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    isQualificationComplete: {
      type: ['boolean', 'null'],
    },
    isRemote: {
      type: ['boolean', 'null'],
    },
    isSynced: {
      type: ['boolean'],
    },
    jobName: {
      type: 'string',
      maxLength: 255,
    },
    matterId: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    nextStep: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    renewal: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    renewalContract: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    offering: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    opportunityOwnerSalesforceId: {
      type: ['string', 'null'],
    },
    ownerUserId: {
      type: ['string', 'null'],
    },
    qualifiedAt: {
      type: ['string', 'null'],
      format: 'date-time',
    },
    reasonLost: {
      type: ['string', 'null'],
    },
    region: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    requiredNumResources: {
      type: ['integer', 'null'],
    },
    salesCloseDate: {
      type: ['string', 'null'],
      format: 'date',
    },
    salesforceAccountId: {
      type: ['string', 'null'],
    },
    salesforceContactId: {
      type: ['string', 'null'],
    },
    salesforceContractId: {
      type: ['string', 'null'],
    },
    salesforceCxLeadId: {
      type: ['string', 'null'],
    },
    salesforceId: {
      type: 'string',
      maxLength: 255,
    },
    salesLeadId: {
      type: ['string', 'null'],
    },
    salesNote: {
      type: ['string', 'null'],
    },
    stage: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    startDate: {
      type: ['string', 'null'],
      format: 'date',
    },
    statusUpdatedAt: {
      type: ['string', 'null'],
      format: 'date-time',
    },
    useCase: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    salesforceQuoteId: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    accountId: {
      type: ['string', 'null'],
    },
    directEligible: {
      type: 'boolean',
    },
    selfService: {
      type: 'boolean',
    },
    stopDirectInterestEmails: {
      type: 'boolean',
    },
    addressCountryCode: {
      type: ['string', 'null'],
      enum: [...OpportunityAddressCountryCodes, null],
    },
    requiredTalentCountryCode: {
      type: ['string', 'null'],
      enum: [...OpportunityAddressCountryCodes, null],
    },
    addressStreet: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    addressCity: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    addressState: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    addressZip: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    drivingNeed: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    opportunityLostCoveragePlan: {
      type: ['string', 'null'],
    },
    workLocation: {
      type: ['string', 'null'],
      enum: [...Object.keys(OpportunitiesConst.WorkLocation), null],
    },
    talentInMind: {
      type: ['string', 'null'],
      enum: [...Object.keys(YesNoMaybeConst.YesNoMaybe), null],
    },
    laptopProvider: {
      type: ['string', 'null'],
      enum: [...OpportunityLaptopProviders, null],
    },
    excludedFromFeedUserOverride: {
      type: ['boolean', 'null'],
    },
    allowInterest: {
      type: ['boolean', 'null'],
    },
    clientBudgetConfirmed: {
      type: ['string', 'null'],
      enum: [...Object.keys(OpportunitiesConst.ClientBudgetConfirmed), null],
    },
    clientBudgetMin: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: getMaxFloatValue(18, 2),
    },
    clientBudgetMax: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: getMaxFloatValue(18, 2),
    },
    submissionPublishedAt: {
      type: ['string', 'null'],
      format: 'date-time',
    },
    transactionSubType: {
      type: ['string', 'null'],
      enum: [
        TransactionSubType.Backfill,
        TransactionSubType.AdditionalResource,
        TransactionSubType.NewDeal,
        null,
      ],
    },
  },
};
const createOpportunityValidation = opportunityCommonValidation;

const replaceOpportunityValidation = {
  ...opportunityCommonValidation,
  properties: {
    ...opportunityCommonValidation.properties,
    primaryPracticeArea: {
      type: ['string', 'null'],
      enum: [
        ...(Object.values(PracticeArea) as string[]),
        'unknown',
        'Unknown',
        null,
      ],
    },
    primaryFocusArea: {
      type: ['string', 'null'],
      enum: [
        ...(Object.values(FocusArea) as string[]),
        'unknown',
        'Unknown',
        null,
      ],
    },
    supervisingLawyerContactId: {
      type: ['string', 'null'],
    },
    supervisingLawyerCountryCode: {
      type: ['string', 'null'],
      enum: [...OpportunityAddressCountryCodes, null],
    },
    supervisingLawyerState: {
      type: ['string', 'null'],
      maxLength: 255,
    },
  },
};

const updateOpportunityValidation = {
  ...opportunityCommonValidation,
  anyOf: Object.keys(opportunityCommonValidation.properties).map(key => ({
    required: [key],
  })),
};

const opportunityFilterValidation = {
  type: 'object',
  additionalProperties: false,
  properties: {
    id: {
      type: 'array',
      items: {
        type: 'string',
        format: 'uuid',
      },
    },
    stage: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    stageCode: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    businessTeam: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    salesLeadId: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    salesforceId: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    ownerUserId: {
      type: 'array',
      items: {
        type: 'string',
        format: 'uuid',
      },
    },
    startDate: {
      type: 'object',
      additionalProperties: false,
      properties: {
        end: {
          type: 'string',
          format: 'date',
        },
      },
    },
    salesCloseDate: {
      type: 'object',
      additionalProperties: false,
      properties: {
        start: {
          type: 'string',
          format: 'date',
        },
        end: {
          type: 'string',
          format: 'date',
        },
      },
    },
    legal: {
      type: 'boolean',
    },
    nonLegal: {
      type: 'boolean',
    },
    accountName: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    engagementType: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    offering: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    isFulfillmentActive: {
      type: 'boolean',
    },
  },
};

// VALIDATORS
export const createOpportunityValidator = ajv.compile(
  createOpportunityValidation
);
export const createOpportunityArrayValidator = arrayValidatorCreator(
  createOpportunityValidation
);

export const replaceOpportunityValidatorCreator = ({
  ajvInstance = ajv,
  additionalOpts = {},
} = {}) =>
  ajvInstance.compile({ ...replaceOpportunityValidation, ...additionalOpts });

export const replaceOpportunityValidator = replaceOpportunityValidatorCreator();

// ONLY CONDITIONALLY USE THIS - for boomi endpoints only
export const replaceOpportunityAdditionalPropertiesValidator =
  replaceOpportunityValidatorCreator({
    ajvInstance: new Ajv({
      ...axiomValidationOptions(),
      removeAdditional: true,
    }),
    additionalOpts: {
      additionalProperties: false,
    },
  });

export const updateOpportunityValidator = ajv.compile(
  updateOpportunityValidation
);

export const opportunityFilterValidator = ajv.compile({
  ...opportunityFilterValidation,
});

export const opportunitySortTermValidator = ajvNoCoerce.compile({
  type: 'object',
  additionalProperties: false,
  properties: {
    sort: {
      type: 'string',
      enum: OpportunityConst.SORT_COLUMNS,
    },
  },
});

export const opportunityStatsFilterValidator = ajv.compile({
  type: 'object',
  additionalProperties: false,
  properties: {
    ...opportunityFilterValidation.properties,
    'positions.startDate': {
      type: 'object',
      additionalProperties: false,
      properties: {
        start: {
          type: 'string',
          format: 'date',
        },
        end: {
          type: 'string',
          format: 'date',
        },
      },
    },
  },
});

export const stageCodeValidation = {
  type: 'array',
  items: {
    type: 'string',
    enum: StageCodes,
  },
  uniqueItems: true,
};

export const stageCodeValidator = ajv.compile(stageCodeValidation);
